/**
 * Trusted-Adult / Safeguarding Contacts Panel
 *
 * Lets a parent configure the trusted adults the safety layer routes serious
 * disclosures to. Abuse & grooming disclosures go to a safeguarding contact —
 * never automatically to the account owner, who may be the source of harm.
 * Styled to match the Safety Dashboard.
 */
import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/integrations/api';
import { toast } from 'sonner';
import { ShieldCheck, Plus, Trash2, Pencil, Mail, Phone, X, Loader2 } from 'lucide-react';

const FONT = "'DM Sans', sans-serif";
const C = {
  heading: '#2A2926', body: '#6B675E', muted: '#9B978E',
  border: '#E8E6E1', teal: '#3ECDC6', white: '#FFFFFF', amberBg: '#FDF6E8', amber: '#D4A843',
};

interface TrustedAdult {
  id: string;
  child_profile_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  relationship: string | null;
  is_safeguarding_contact: boolean;
  created_at: string;
}

type FormState = {
  name: string; email: string; phone: string; relationship: string; is_safeguarding_contact: boolean;
};
const EMPTY_FORM: FormState = { name: '', email: '', phone: '', relationship: '', is_safeguarding_contact: true };

export function TrustedAdultsPanel() {
  const [contacts, setContacts] = useState<TrustedAdult[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get<TrustedAdult[]>('/safety-settings/trusted-adults')
      .then((res) => setContacts(res.data))
      .catch(() => {/* table may not exist until migration 073 runs */})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (c: TrustedAdult) => {
    setEditingId(c.id);
    setForm({ name: c.name, email: c.email ?? '', phone: c.phone ?? '', relationship: c.relationship ?? '', is_safeguarding_contact: c.is_safeguarding_contact });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Please enter a name'); return; }
    if (!form.email.trim() && !form.phone.trim()) { toast.error('Add an email or phone so we can reach this contact'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      relationship: form.relationship.trim() || null,
      is_safeguarding_contact: form.is_safeguarding_contact,
    };
    try {
      if (editingId) {
        await apiClient.put(`/safety-settings/trusted-adults/${editingId}`, payload);
        toast.success('Contact updated');
      } else {
        await apiClient.post('/safety-settings/trusted-adults', payload);
        toast.success('Trusted adult added');
      }
      closeForm();
      load();
    } catch {
      toast.error('Could not save the contact');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    const prev = contacts;
    setContacts((cs) => cs.filter((c) => c.id !== id));
    try {
      await apiClient.delete(`/safety-settings/trusted-adults/${id}`);
      toast.success('Contact removed');
    } catch {
      setContacts(prev);
      toast.error('Could not remove the contact');
    }
  };

  const input = (label: string, key: keyof FormState, type = 'text', placeholder = '') => (
    <div className="flex flex-col gap-1.5">
      <label style={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: C.body }}>{label}</label>
      <input
        type={type}
        value={form[key] as string}
        placeholder={placeholder}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="rounded-lg border px-3 py-2"
        style={{ fontFamily: FONT, fontSize: 14, borderColor: C.border, color: C.heading, outline: 'none' }}
      />
    </div>
  );

  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} style={{ color: C.teal }} />
        <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 600, color: C.heading }}>Safeguarding Contacts</h2>
      </div>
      <p style={{ fontFamily: FONT, fontSize: 14, color: C.muted, marginBottom: 16 }}>
        Trusted adults Luno can alert if a child shares something serious. Disclosures about being unsafe at home are sent
        to a safeguarding contact here — never automatically to you — so a child is protected even when the concern is closer to home.
      </p>

      <div className="bg-white rounded-xl border" style={{ borderColor: C.border }}>
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 size={18} className="animate-spin" style={{ color: C.muted }} />
          </div>
        ) : contacts.length === 0 && !showForm ? (
          <div className="p-8 text-center">
            <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: C.heading, marginBottom: 4 }}>No safeguarding contact yet</p>
            <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 16 }}>
              Add a trusted adult — a second parent, relative, or family friend — who should be told if a serious concern comes up.
            </p>
            <button onClick={openAdd} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg transition hover:opacity-90"
              style={{ fontFamily: FONT, fontSize: 14, background: C.teal, color: C.white, fontWeight: 600 }}>
              <Plus size={15} /> Add a trusted adult
            </button>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: C.border }}>
            {contacts.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-5 gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: C.heading }}>{c.name}</p>
                    {c.relationship && (
                      <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>· {c.relationship}</span>
                    )}
                    {c.is_safeguarding_contact && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                        style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, background: '#E8F6F4', color: '#1F8F88' }}>
                        <ShieldCheck size={11} /> Safeguarding
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 flex-wrap">
                    {c.email && (
                      <span className="inline-flex items-center gap-1.5" style={{ fontFamily: FONT, fontSize: 13, color: C.body }}>
                        <Mail size={13} style={{ color: C.muted }} /> {c.email}
                      </span>
                    )}
                    {c.phone && (
                      <span className="inline-flex items-center gap-1.5" style={{ fontFamily: FONT, fontSize: 13, color: C.body }}>
                        <Phone size={13} style={{ color: C.muted }} /> {c.phone}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(c)} className="p-2 rounded-lg transition hover:bg-gray-50" aria-label="Edit contact">
                    <Pencil size={15} style={{ color: C.muted }} />
                  </button>
                  <button onClick={() => remove(c.id)} className="p-2 rounded-lg transition hover:bg-red-50" aria-label="Remove contact">
                    <Trash2 size={15} style={{ color: '#D4574E' }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div className="p-5 border-t" style={{ borderColor: C.border, background: '#FBFAF7' }}>
            <div className="flex items-center justify-between mb-4">
              <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: C.heading }}>
                {editingId ? 'Edit contact' : 'New trusted adult'}
              </p>
              <button onClick={closeForm} className="p-1 rounded-md transition hover:bg-gray-100" aria-label="Close">
                <X size={16} style={{ color: C.muted }} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {input('Name', 'name', 'text', 'e.g. Aunt Sara')}
              {input('Relationship', 'relationship', 'text', 'e.g. Aunt, family friend')}
              {input('Email', 'email', 'email', 'name@example.com')}
              {input('Phone', 'phone', 'tel', 'optional')}
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex-1 mr-4">
                <p style={{ fontFamily: FONT, fontSize: 14, fontWeight: 500, color: C.heading }}>Receives safeguarding alerts</p>
                <p style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>Serious disclosures (e.g. being unsafe at home) are sent to this person instead of you.</p>
              </div>
              <button
                onClick={() => setForm((f) => ({ ...f, is_safeguarding_contact: !f.is_safeguarding_contact }))}
                className="relative flex-shrink-0 rounded-full transition-colors duration-200 overflow-hidden"
                style={{ minWidth: 44, width: 44, height: 24, background: form.is_safeguarding_contact ? C.teal : C.border }}
                aria-label="Toggle safeguarding"
              >
                <span className="absolute rounded-full bg-white shadow transition-transform duration-200"
                  style={{ width: 20, height: 20, top: 2, left: 0, transform: form.is_safeguarding_contact ? 'translateX(22px)' : 'translateX(2px)' }} />
              </button>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={save} disabled={saving}
                className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg transition hover:opacity-90 disabled:opacity-50"
                style={{ fontFamily: FONT, fontSize: 14, background: C.teal, color: C.white, fontWeight: 600 }}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : null}
                {editingId ? 'Save changes' : 'Add contact'}
              </button>
              <button onClick={closeForm}
                className="px-5 py-2 rounded-lg border transition hover:bg-gray-50"
                style={{ fontFamily: FONT, fontSize: 14, borderColor: C.border, color: C.body, fontWeight: 500 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add button when list is non-empty and form closed */}
      {!loading && contacts.length > 0 && !showForm && (
        <button onClick={openAdd} className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg border transition hover:bg-gray-50"
          style={{ fontFamily: FONT, fontSize: 14, borderColor: C.border, color: C.body, fontWeight: 500 }}>
          <Plus size={15} /> Add another contact
        </button>
      )}
    </div>
  );
}
